'use strict';
const {getValidatingCarrier} = require('od-modules/utils/provider_rules');
const {BIM_CANCELLATION_TYPE, BIM_STATUS_GROUPS} = require('components/user_area/incidents_manager/js/utils/constants');
const {
    PNR_LIST_JOIN,
    EMPTY_STRING,
    FLIGHT_STATUS,
    BOOKING_STATUS,
    AVAILABLE_SUPPORT_OPTIONS,
    AVAILABLE_INSURANCES,
    CSS_CLASSES,
    getMyTripStatusOptions,
    MANAGE_BOOKING_STATUS_ALLOWED,
} = require('./constants');
const MMBButton = require('components/mmb_button/js/main');
const {BIM_TYPE} = require('../../../incidents_manager/js/utils/constants');

/* NOT EXPORTED HELPER FUNCTIONS */
const sanitizeInsuranceBookings = (insuranceBookings) =>
    (insuranceBookings || [])
        .filter(function(booking) {
            let policy = Odigeo._.get(booking, 'insurance.policy');
            if (policy) {
                booking.insurance.policy = policy.replace(/-/g, '_');
                return Odigeo._.includes(AVAILABLE_INSURANCES, booking.insurance.policy);
            }
            return false;
        });

const sanitizeSupportOptions = (supportOptionsProducts) =>
    (supportOptionsProducts || [])
        .filter(function(product) {
            let productId = Odigeo._.get(product, 'name');
            if (productId) {
                product.name = productId.replace(/-/g, '_');
                return Odigeo._.includes(Odigeo._.keys(AVAILABLE_SUPPORT_OPTIONS), product.name.toUpperCase());
            }
            return false;
        })
        .map((product) => Odigeo._.extend(product, AVAILABLE_SUPPORT_OPTIONS[product.name]));

const generatePNRList = (itinerarySegments) =>
    Odigeo._.union((itinerarySegments || [])
        .map((segment) => segment.sections).flat()
        .map((section) => Odigeo._.get(section, 'section.pnr'))
        .filter((pnr) => !Odigeo._.isNull(pnr)));

const extendPNRInfo = (pnrInfo = {}, section, index, isTripDetails) => {
    const validatingCarrier = getValidatingCarrier(section);
    if (Odigeo.AB.isBookingImportantMessage && isTripDetails) {
        const {provider} = section;
        const {sections = []} = pnrInfo;
        return {
            ...pnrInfo,
            provider,
            validatingCarrier,
            sections: [...sections, section],
        };
    } else {
        const {merchant, provider} = section;
        const isSectionCancelled = isFlightStatusCancelled(section);
        let {sections = [], sectionsCancelled = [], hasSomeSectionsCancelled = false, hasAllSectionsCancelled = true, sectionsIndex = []} = pnrInfo;
        if (isSectionCancelled) {
            hasSomeSectionsCancelled = true;
            sectionsCancelled.push(section);
        }
        hasAllSectionsCancelled = hasAllSectionsCancelled && isSectionCancelled;
        sections.push(section);

        if (!sectionsIndex.includes(index)) {
            sectionsIndex.push(index);
        }
        return {
            merchant,
            hasSomeSectionsCancelled,
            hasAllSectionsCancelled,
            sectionsCancelled,
            provider,
            validatingCarrier,
            sections,
            sectionsIndex,
        };
    }
};

const getBookingPNRsInfo = ({itineraryBookings: {itinerarySegments}} = {itineraryBookings: {}}, isTripDetails = false) =>
    (itinerarySegments || [])
        .map(({sections}) => sections || [])
        .flat()
        .reduce((acc, data, index) => {
            const {pnr = 'UNKNOWN'} = data.section;
            acc[pnr] = extendPNRInfo(acc[pnr], data, index, isTripDetails);
            return acc;
        }, {});

const getCancelledPNRs = (bookingPNRsInfo) => Odigeo._.pick(bookingPNRsInfo, (pnr) => pnr.hasSomeSectionsCancelled);

const isFlightStatusCancelled = (sectionData) => {
    const sectionStatus = Odigeo._.get(sectionData, 'section.flightStatus', '');
    return sectionStatus === FLIGHT_STATUS.CANCELLED;
};

const areAllItinerarySectionsCancelled = (bookingPNRsInfo) => Object.values(bookingPNRsInfo).every((pnrInfo) => pnrInfo.hasAllSectionsCancelled);

const shouldHideAllProductOffers = ({myTripStatus}) => myTripStatus === BOOKING_STATUS.CANCELLED || myTripStatus === BOOKING_STATUS.ALTERED;

const extendBookingDetailForTripdetails = (bookingDetail) => {
    const segments = Odigeo._.get(bookingDetail, 'itineraryBookings.itinerarySegments', []);
    const tripType = Odigeo._.get(bookingDetail, 'itineraryBookings.bookingItinerary.tripType');
    const PNRList = generatePNRList(segments);

    bookingDetail.shouldHideAllProductOffers = shouldHideAllProductOffers(bookingDetail);
    bookingDetail.listOfPnrsInBooking = PNRList.join(PNR_LIST_JOIN);
    bookingDetail.totalPnrsInBooking = PNRList.length;
    bookingDetail.hasBottomBannerContextualOffers = Odigeo.Utils.isDesktop() && !bookingDetail.shouldHideAllProductOffers && (tripType !== 'ONE_WAY');
    bookingDetail.insuranceBookings.bookings = sanitizeInsuranceBookings(bookingDetail.insuranceBookings.bookings);

    if (!Odigeo._.isEmpty(bookingDetail.postsellServiceOptionBookings)) {
        bookingDetail.postsellServiceOptionBookings
            .forEach((psob) => psob.postsellServiceOptionProducts = sanitizeSupportOptions(psob.postsellServiceOptionProducts));
    }
};

const updateBookingDetail = (bookingDetail, widgetKeys) => {
    const tripTypeClass = `od-${Odigeo._.get(bookingDetail, 'itineraryBookings.bookingItinerary.tripType', 'hsa')}`.toLowerCase().replace('_', '-');
    updateBuyerName(bookingDetail.buyer);
    Object.assign(bookingDetail,
        getMyTripStatusOptions(bookingDetail, widgetKeys),
        getTripCardData(bookingDetail, widgetKeys),
        {
            showManageMyBookingBtn: Odigeo.AB.isManageMyBookingMyTrips && MANAGE_BOOKING_STATUS_ALLOWED.includes(bookingDetail.bookingProductStatus),
            mmbButton: MMBButton.renderAsButton(),
            formatTotalAmount: formatPrice(bookingDetail.price),
            tripTypeClass,
        }
    );
};

const checktMyTripStatus = (bookingDetail, isTripDetails) => {
    const {cancelledPNRs, myTripStatus, bookingProductStatus, messages, messagesType} = bookingDetail;
    let hasMessages;
    if (Odigeo.AB.isAlignVoluntaryCancellation) {
        hasMessages = !Odigeo.AB.isBookingImportantMessage || messages &&
            messages.some(({type}) => BIM_TYPE.CANCELLATION === type);
    } else {
        hasMessages =
            !Odigeo.AB.isBookingImportantMessage || messagesType &&
            messagesType.some((type) => BIM_CANCELLATION_TYPE.INVOLUNTARY === type);
    }
    const shouldOverwriteStatusToAltered = (hasMessages || !isTripDetails) &&
      (!Odigeo._.isEmpty(cancelledPNRs)) && (myTripStatus !== BOOKING_STATUS.CANCELLED);
    bookingDetail.myTripStatus = shouldOverwriteStatusToAltered ? BOOKING_STATUS.ALTERED : myTripStatus;
    bookingDetail.bookingProductStatus = shouldOverwriteStatusToAltered ? BOOKING_STATUS.ALTERED : bookingProductStatus;
};

const updateBuyerName = (buyer) => {
    const {name = '', lastNames = ''} = buyer;
    if (name) {
        buyer.name = name.toLowerCase();
    }
    if (lastNames) {
        buyer.lastNames = lastNames.toLowerCase();
    }
};

const getTripCardData = ({itineraryBookings = null, myTripStatus, bookingProductStatus, accommodationBooking}, widgetKeys) => {
    let data;
    const isMobile = Odigeo.Utils.isMobile();
    if (itineraryBookings !== null) {
        const {bookingItinerary} = itineraryBookings;
        const {
            transportType = 'FLIGHT',
            tripType,
            departureDate,
            departure,
            arrivalDate,
            arrival,
        } = bookingItinerary || {};
        const {cityIataCode: arrivalIATACode, cityName: arrivalCityName} = arrival || {};
        const {cityName: departureCityName} = departure || {};
        const daysToDeparture = Odigeo.Utils.millisecsToDays(departureDate);
        data = {
            ...CSS_CLASSES.ONE_TRIP_CARD_FALLBACK.ITINERARY[transportType],
            tripCardImageUrl: `${Odigeo.UserArea.imagePath}onefront/destinations/${arrivalIATACode}_${isMobile ? '640x480' : '1920x800'}.jpg`,
            bookingTitle: tripType === 'MULTI_SEGMENT' ?
                widgetKeys.myTripsBookingsMultiTripTitle :
                isMobile ? arrivalCityName : `${departureCityName} ${widgetKeys.myTripsBookingsTo} ${arrivalCityName}`,
            bookingDates: `${humanTripDate(departureDate)}${(tripType !== 'ONE_WAY') ? ' - ' + humanTripDate(arrivalDate) : ''}`,
            daysToDeparture: daysToDeparture && Odigeo.Utils.pluralizeAll(widgetKeys.daysPlural, daysToDeparture),
            bookingStatusDescription: widgetKeys[`${transportType.toLowerCase()}StatusText${myTripStatus}`],
        };
    } else {
        const {hotelImage, accommodationName, checkInDate, checkOutDate, city} = accommodationBooking || {};
        const {url} = hotelImage || {};
        const daysToCheckIn = Odigeo.Utils.millisecsToDays(checkInDate || 0);
        data = {
            ...CSS_CLASSES.ONE_TRIP_CARD_FALLBACK.ACCOMMODATION,
            tripCardImageUrl: url || `${Odigeo.UserArea.imagePath}onefront/bluestone/images/${isMobile ? '1_1' : '16_9'}_1x.png`,
            bookingTitle: `${accommodationName}${city ? ', ' + city : ''}`,
            bookingDates: `${humanTripDate(checkInDate)} - ${humanTripDate(checkOutDate)}`,
            daysToDeparture: daysToCheckIn && Odigeo.Utils.pluralizeAll(widgetKeys.daysPlural, daysToCheckIn),
            bookingStatusDescription: `${widgetKeys['hotelStatusText' + myTripStatus]}`,
        };
    }
    return data;
};

const humanTripDate = (date) => {
    const formatByWebsite = {
        US: 'ddd, dd mmm yyyy',
    };
    return Odigeo.Utils.formatDate(date, formatByWebsite[Odigeo.SessionScope.website] || 'ddd, mmm dd yyyy', true);
};

const isAccommodationBooking = ({accommodationBooking}) => !Odigeo._.isEmpty(accommodationBooking);

const isFlightTransportType = (bookingDetail) => bookingDetail?.itineraryBookings?.bookingItinerary?.transportType === 'FLIGHT';

const extendBookingDetailWithBIMData = (bookingDetail) => {
    const {cancelledPNRs, extendedIncidents, messagesType} = obtainData(bookingDetail);
    bookingDetail.cancelledPNRs = cancelledPNRs;
    bookingDetail.incidents = extendedIncidents;
    bookingDetail.messagesType = messagesType;
};

const obtainData = ({messages, bookingPNRs = {}}) => {
    let messageType;
    return (messages || [])
        .map(({cancellations, cancellationType}) => {
            messageType = cancellationType;
            return cancellations;
        })
        .flat()
        .reduce((data, cancellation) => {
            obtainIncidentsByMsg(cancellation, data);
            obtainCancelledPNRs(cancellation, data, bookingPNRs);
            data.messagesType.push(messageType);
            return data;
        }, {
            extendedIncidents: [],
            cancelledPNRs: {},
            messagesType: [],
        });
};

const obtainCancelledPNRs =
    ({pnr, cancellationStatus, refund, incidents, cancellationProviderItem, providerBookingItemId, merchant}, {cancelledPNRs}, bookingPNRs) => {
        const {
            provider,
            validatingCarrier,
            sections,
            sectionsIndex,
        } = bookingPNRs[pnr];
        const sectionsCancelled = getSectionsCancelled(incidents, providerBookingItemId, sections);

        cancelledPNRs[pnr] = {
            provider,
            validatingCarrier,
            sections,
            sectionsIndex,
            merchant,
            refund,
            incidents,
            sectionsCancelled,
            ...getAmountItems(cancellationProviderItem),
            statusGroup: getStatusGroup(cancellationStatus),
            cancellationStatus,
            hasAllSectionsCancelled: sectionsCancelled.length === sections.length,
            hasSomeSectionsCancelled: true,
            incidentInfo: obtainIncidentInfo(incidents),
        };
    };

const getAmountItems = (cancellationProviderItem) => {
    const {cashRefundAmount, openTicketTotalAmount, primeSubscriptionAmount, topUpVoucherAmount} = cancellationProviderItem || {};
    return {
        cashRefundAmount: getAmount(cashRefundAmount),
        openTicketTotalAmount: getAmount(openTicketTotalAmount),
        topUpVoucherAmount: getAmount(topUpVoucherAmount),
        primeSubscriptionAmount: getAmount(primeSubscriptionAmount),
    };
};

const getAmount = (data) => {
    const {amount, currencyConfig} = data || {};
    return typeof amount === 'number' ?
        formatPrice({amount, currencyConfig: {...currencyConfig, ...{decimalPlaces: 0}}}) :
        EMPTY_STRING;
};

const getStatusGroup = (status) => Object.keys(BIM_STATUS_GROUPS).filter((key) => BIM_STATUS_GROUPS[key].includes(status))[0];

const getUTCIncidentDate = (incidentDate) => {
    const date = new Date(incidentDate);
    return Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds());
};

const getSectionsCancelled = (incidents, providerBookingItemId, sections) => (incidents || [])
    .map((incident) =>
        sections.filter((section) => isEqualIncident(section, incident, providerBookingItemId)))
    .flat();

const obtainIncidentsByMsg = ({incidents, providerBookingItemId, cancellationId, cancellationStatus}, {extendedIncidents}) => {
    (incidents || []).forEach((incident) => {
        extendedIncidents.push({
            ...incident,
            ...{
                providerBookingItemId,
                cancellationId,
                status: cancellationStatus,
            },
        });
    });
};

const obtainIncidentInfo = (incidents) => (incidents || []).reduce((incidentInfo, incident) => ({
    creationDateTime: getIncidentDateTime(incidentInfo, incident, 'creationDate'),
    departureDateTime: getIncidentDateTime(incidentInfo, incident, 'departureDate'),
    source: incident.source,
}), {});


const getIncidentDateTime = (incidentInfo, incident, label) => {
    const timeLabel = `${label}Time`;
    if (incident[label]) {
        const newDate = new Date(incident[label]).getTime();
        return incidentInfo[timeLabel] && incidentInfo[timeLabel] < newDate ? incidentInfo[timeLabel] : newDate;
    } else {
        return incidentInfo[timeLabel];
    }
};

const isBookingDeparted = (bookingDetail) => {
    return bookingDetail.itineraryBookings.bookingItinerary.departureDate < new Date().getTime();
};

// EXPORTED FUNCTIONS
const getTripTypeAbbr = (itineraryBookings) =>
    Odigeo._.get(itineraryBookings, 'bookingItinerary.tripType', '')
        .split('_')
        .map((word) => word.charAt(0))
        .join('')
        .replace('S', 'D'); // OW,RT,MD

const extendBookingDetail = (bookingInfo, widgetKeys, isTripDetails) => {
    if (bookingInfo.itineraryBookings !== null) {
        extendBookingDetailWithFlightStatusData(bookingInfo, isTripDetails);
    }
    updateBookingDetail(bookingInfo, widgetKeys);
    return bookingInfo;
};

const extendBookingDetailWithFlightStatusData = (bookingDetail, isTripDetails = false) => {
    bookingDetail.bookingPNRs = bookingDetail.bookingPNRs || getBookingPNRsInfo(bookingDetail, isTripDetails);
    if (Odigeo.AB.isBookingImportantMessage && isTripDetails) {
        extendBookingDetailWithBIMData(bookingDetail);
    } else {
        bookingDetail.cancelledPNRs = bookingDetail.cancelledPNRs || getCancelledPNRs(bookingDetail.bookingPNRs);
    }
    bookingDetail.areAllItinerarySectionsCancelled =
        bookingDetail.areAllItinerarySectionsCancelled || areAllItinerarySectionsCancelled(bookingDetail.cancelledPNRs);
    checktMyTripStatus(bookingDetail, isTripDetails);
    if (isTripDetails) {
        extendBookingDetailForTripdetails(bookingDetail);
    }
};

const hasBookingAnyIncident = (bookingDetail) => bookingDetail.myTripStatus === BOOKING_STATUS.ALTERED;

const hasMessages = ({messages}) => messages && !!messages.length;

const isEqualIncident =
    ({itineraryBookingId, section: {carrier: {code}, id, departureDate}}, {departureDate: incidentDate, flightNumber}, providerBookingItemId) =>
        itineraryBookingId === parseInt(providerBookingItemId, 10) &&
        flightNumber === `${code}${id}` &&
        getUTCIncidentDate(incidentDate) === departureDate;

const formatPrice = ({amount, currencyConfig}) => {
    const {currencySymbol: currencySign = Odigeo.Utils.Currency.currencySign, ...otherProps} = currencyConfig || {};
    const bookingCurrency = Object.assign({}, Odigeo.Utils.Currency, Odigeo._.isObject(currencyConfig) ? {currencySign, ...otherProps} : {});
    return Odigeo.Utils.formatCurrency(amount, bookingCurrency);
};

const shouldShowCYJFlightCard = (bookingDetail, keys) => (
    isFlightTransportType(bookingDetail) &&
    keys.completeYourJourneyIsAvailable === 'true' &&
    !bookingDetail.shouldHideAllProductOffers &&
    ![BOOKING_STATUS.ALTERED, BOOKING_STATUS.CANCELLED].includes(bookingDetail.bookingProductStatus) &&
    !hasMessages(bookingDetail) &&
    !isAccommodationBooking(bookingDetail) &&
    !bookingDetail.checkinConditions.autoCheckInProduct &&
    !isBookingDeparted(bookingDetail)
);

module.exports = {
    getTripTypeAbbr,
    extendBookingDetail,
    extendBookingDetailWithFlightStatusData,
    hasBookingAnyIncident,
    hasMessages,
    isEqualIncident,
    formatPrice,
    shouldShowCYJFlightCard,
};
